import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, ArrowLeft, RefreshCw, MessageSquare, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { AnalysisResult } from "@/types/analysis";
import { ScoreOverview } from "@/components/report/ScoreOverview";
import { SEOAuditTab } from "@/components/report/SEOAuditTab";
import { AIVisibilityTab } from "@/components/report/AIVisibilityTab";
import { BacklinkTab } from "@/components/report/BacklinkTab";
import { CompetitorTab } from "@/components/report/CompetitorTab";
import { RankingTab } from "@/components/report/RankingTab";
import { ActionPlanTab } from "@/components/report/ActionPlanTab";
import { KeywordsTab } from "@/components/report/KeywordsTab";
import { ContentTab } from "@/components/report/ContentTab";
import FeedbackBanner from "@/components/FeedbackBanner";
import FeedbackModal from "@/components/FeedbackModal";
import ReportChat from "@/components/report/ReportChat";
import UpgradeOverlay from "@/components/UpgradeOverlay";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription, canAccess } from "@/hooks/useSubscription";
import { useCompletedActions } from "@/hooks/useCompletedActions";
import { usePageMeta } from "@/hooks/usePageMeta";
import { supabase } from "@/integrations/supabase/client";

const ReportPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [analysisId, setAnalysisId] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState("seo");
  const [showFeedback, setShowFeedback] = useState(false);
  const { user } = useAuth();
  const { plan } = useSubscription();
  const reportUrl = result?.url || "";

  usePageMeta({
    title: reportUrl ? `SEO Report for ${reportUrl} — Unranked` : "SEO Report — Unranked",
    description: reportUrl ? `SEO audit results for ${reportUrl}.` : "SEO audit results and analysis.",
    canonical: "https://unrankedagency.com/report",
  });

  const completedActions = useCompletedActions();

  useEffect(() => {
    const dataParam = searchParams.get("data");
    if (!dataParam) {
      navigate("/");
      return;
    }
    try {
      const decoded = JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(dataParam)))));
      setResult(decoded);
    } catch {
      navigate("/");
    }
  }, [searchParams]);

  // Resolve the saved analysis row id for this URL so "Keep" can persist
  useEffect(() => {
    if (!user || !result?.url) {
      setAnalysisId(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("analyses")
        .select("id")
        .eq("user_id", user.id)
        .eq("url", result.url)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setAnalysisId(data?.id);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, result?.url]);

  if (!result) return null;

  const hasActionPlan = canAccess(plan, "actionPlan");
  const hasCompetitor = canAccess(plan, "competitor");
  const hasLinkIntel = canAccess(plan, "linkIntelligence");
  const hasContent = canAccess(plan, "content");
  const hasAiChat = canAccess(plan, "aiChat");

  return (
    <div className="min-h-screen bg-background">
      {(!user || plan === "free") && (
        <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2">
                <img src="/Unranked-agency-logo.png" alt="Unranked Agency" className="h-6 sm:h-7 w-auto object-contain max-w-[140px] sm:max-w-none" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground hidden sm:block truncate max-w-xs">
                {result.url}
              </div>
              {searchParams.get("email") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={() => navigate(`/analyze?url=${encodeURIComponent(result.url)}&email=${encodeURIComponent(searchParams.get("email")!)}`)}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Re-analyze
                </Button>
              )}
            </div>
          </div>
        </nav>
      )}

      <div className="container mx-auto px-6 py-8">
        <ScoreOverview result={result} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="w-full justify-start bg-card border border-border h-auto p-1 flex-wrap gap-1">
            <TabsTrigger value="seo" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">SEO Audit</TabsTrigger>
            <TabsTrigger value="keywords" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Keywords</TabsTrigger>
            <TabsTrigger value="ai" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">AI Visibility</TabsTrigger>
            <TabsTrigger value="backlinks" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Link Intelligence</TabsTrigger>
            <TabsTrigger value="competitors" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Competitors</TabsTrigger>
            <TabsTrigger value="ranking" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Ranking</TabsTrigger>
            <TabsTrigger value="actions" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Action Plan
              {!hasActionPlan && <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 ml-1">Pro</Badge>}
            </TabsTrigger>
            <TabsTrigger value="content" disabled={!hasContent} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5">
              Content
              {!hasContent && <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 ml-1">Growth</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="seo"><SEOAuditTab data={result.seoAudit} onViewActionPlan={() => setActiveTab("actions")} /></TabsContent>
          <TabsContent value="keywords"><KeywordsTab data={result.keywordAnalysis} /></TabsContent>
          <TabsContent value="ai"><AIVisibilityTab data={result.aiVisibility} backlinkScore={result.backlinkProfile.overallScore} /></TabsContent>
          
          <TabsContent value="backlinks">
            {!hasLinkIntel && result.linkIntelligence ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {[
                    { label: "Total Links", value: result.linkIntelligence.totalLinksFound },
                    { label: "Internal Links", value: result.linkIntelligence.internalLinks },
                    { label: "External Links", value: result.linkIntelligence.externalLinks },
                    { label: "Unique External Domains", value: result.linkIntelligence.uniqueExternalDomains },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-card border border-border rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <span>Internal {Math.round(result.linkIntelligence.internalLinkRatio * 100)}%</span>
                    <span className="ml-auto">External {Math.round((1 - result.linkIntelligence.internalLinkRatio) * 100)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                    <div className="bg-primary h-full" style={{ width: `${result.linkIntelligence.internalLinkRatio * 100}%` }} />
                    <div className="bg-accent h-full flex-1" />
                  </div>
                </div>
                <UpgradeOverlay requiredPlan="starter" currentPlan={plan}>
                  <BacklinkTab data={result.backlinkProfile} linkIntelligence={result.linkIntelligence} />
                </UpgradeOverlay>
              </>
            ) : (
              <UpgradeOverlay requiredPlan="starter" currentPlan={plan}>
                <BacklinkTab data={result.backlinkProfile} linkIntelligence={result.linkIntelligence} />
              </UpgradeOverlay>
            )}
          </TabsContent>
          
          <TabsContent value="competitors">
            {!hasCompetitor ? (
              <>
                <div className="bg-card border border-border rounded-lg overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 text-muted-foreground font-medium">Site</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Content</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Keywords</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Technical</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Backlinks</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">AI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const scoreColor = (s: number) => s >= 70 ? "text-success" : s >= 40 ? "text-warning" : "text-destructive";
                        const li = result.linkIntelligence;
                        const backlinkScore = (() => {
                          if (!li) return result.backlinkProfile.overallScore;
                          const authorityScore = li.estimatedReferringDomainsForPage1 > 0
                            ? Math.min(100, Math.round((li.currentEstimatedReferringDomains / li.estimatedReferringDomainsForPage1) * 100))
                            : 100;
                          const factors = [li.internalLinkingScore, authorityScore];
                          if (li.externalLinkQualityScore != null) factors.push(li.externalLinkQualityScore);
                          return Math.round(factors.reduce((a, b) => a + b, 0) / factors.length);
                        })();
                        const scores = [
                          result.seoAudit.contentQuality.score,
                          result.keywordAnalysis?.overallScore ?? 0,
                          result.seoAudit.technical.score,
                          backlinkScore,
                          result.aiVisibility.overallScore,
                        ];
                        return (
                          <tr className="border-b border-border border-l-2 border-l-primary bg-primary/5">
                            <td className="p-3">
                              <div className="font-medium text-sm text-primary">Your Site</div>
                              <div className="text-xs text-muted-foreground truncate max-w-[200px]">{result.url}</div>
                            </td>
                            {scores.map((s, i) => (
                              <td key={i} className={`text-center p-3 font-mono font-bold ${scoreColor(s)}`}>{s}</td>
                            ))}
                          </tr>
                        );
                      })()}
                      {result.competitorIntelligence.competitors.map((c) => (
                        <tr key={c.name} className="border-b border-border last:border-0">
                          <td className="p-3 font-medium text-foreground">{c.name}</td>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <td key={i} className="text-center p-3"><Lock className="w-3 h-3 mx-auto text-muted-foreground/30" /></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mb-4 text-center">Upgrade to Starter to unlock full competitor scores, gap analysis, and improvement opportunities.</p>
                <UpgradeOverlay requiredPlan="starter" currentPlan={plan}>
                  <CompetitorTab data={result.competitorIntelligence} analyzedUrl={result.url} analyzedScores={{
                    contentDepth: result.seoAudit.contentQuality.score,
                    keywordCoverage: result.keywordAnalysis?.overallScore ?? 0,
                    technicalSEO: result.seoAudit.technical.score,
                    backlinkStrength: (() => {
                      const li = result.linkIntelligence;
                      if (!li) return result.backlinkProfile.overallScore;
                      const authorityScore = li.estimatedReferringDomainsForPage1 > 0
                        ? Math.min(100, Math.round((li.currentEstimatedReferringDomains / li.estimatedReferringDomainsForPage1) * 100))
                        : 100;
                      const factors = [li.internalLinkingScore, authorityScore];
                      if (li.externalLinkQualityScore != null) factors.push(li.externalLinkQualityScore);
                      return Math.round(factors.reduce((a, b) => a + b, 0) / factors.length);
                    })(),
                    aiVisibility: result.aiVisibility.overallScore,
                  }} />
                </UpgradeOverlay>
              </>
            ) : (
              <CompetitorTab data={result.competitorIntelligence} analyzedUrl={result.url} analyzedScores={{
                contentDepth: result.seoAudit.contentQuality.score,
                keywordCoverage: result.keywordAnalysis?.overallScore ?? 0,
                technicalSEO: result.seoAudit.technical.score,
                backlinkStrength: (() => {
                  const li = result.linkIntelligence;
                  if (!li) return result.backlinkProfile.overallScore;
                  const authorityScore = li.estimatedReferringDomainsForPage1 > 0
                    ? Math.min(100, Math.round((li.currentEstimatedReferringDomains / li.estimatedReferringDomainsForPage1) * 100))
                    : 100;
                  const factors = [li.internalLinkingScore, authorityScore];
                  if (li.externalLinkQualityScore != null) factors.push(li.externalLinkQualityScore);
                  return Math.round(factors.reduce((a, b) => a + b, 0) / factors.length);
                })(),
                aiVisibility: result.aiVisibility.overallScore,
              }} />
            )}
          </TabsContent>
          
          <TabsContent value="ranking"><RankingTab data={result.rankingPrediction} /></TabsContent>
          
          <TabsContent value="actions">
            {!hasActionPlan && result.actionPlan?.length > 0 ? (
              <>
                <ActionPlanTab data={[result.actionPlan[0]]} pageUrl={result.url} pageContent={result.pageContent} />
                <UpgradeOverlay requiredPlan="starter" currentPlan={plan}>
                  <ActionPlanTab
                    data={result.actionPlan.slice(1)}
                    completedActions={user ? completedActions : undefined}
                    analysisId={analysisId}
                    pageUrl={result.url}
                    pageContent={result.pageContent}
                  />
                </UpgradeOverlay>
              </>
            ) : (
              <UpgradeOverlay requiredPlan="starter" currentPlan={plan}>
                <ActionPlanTab
                  data={result.actionPlan}
                  completedActions={user ? completedActions : undefined}
                  analysisId={analysisId}
                  pageUrl={result.url}
                  pageContent={result.pageContent}
                />
              </UpgradeOverlay>
            )}
          </TabsContent>
          
          <TabsContent value="content">
            {hasContent ? (
              <ContentTab content={result.pageContent} />
            ) : (
              <div className="text-center py-16 text-muted-foreground">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-lg font-medium">Growth Plan Feature</p>
                <p className="text-sm mt-1">Upgrade to the Growth plan to access the content editor.</p>
                <Button size="sm" className="mt-4" onClick={() => navigate("/pricing")}>View Plans</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <FeedbackBanner onFeedbackClick={() => setShowFeedback(true)} />
      </div>

      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} defaultEmail={searchParams.get("email") || ""} />
      
      {hasAiChat ? (
        <ReportChat analysisData={result} />
      ) : (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-1">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 mb-1">Growth</Badge>
          <button
            disabled
            className="w-14 h-14 rounded-full bg-primary/40 text-primary-foreground/60 flex items-center justify-center shadow-lg cursor-not-allowed opacity-60"
            aria-label="Ask AI (Growth plan)"
          >
            <MessageSquare className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
